/**
 * 表单验证工具函数
 */

/**
 * 验证邮箱格式
 * @param email 邮箱地址
 * @returns 是否为有效邮箱
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * 验证密码强度
 * @param password 密码
 * @returns 是否为有效密码
 */
export const validatePassword = (password: string): boolean => {
  // 至少8位数
  if (password.length < 8) {
    return false;
  }

  // 检查是否包含数字、英文字母、特殊符号
  const hasNumber = /\d/.test(password);
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  // 至少包含三种中的两种
  const typeCount = [hasNumber, hasLetter, hasSpecialChar].filter(Boolean).length;
  return typeCount >= 2;
};

/**
 * 获取密码验证错误信息
 * @param password 密码
 * @returns 错误信息
 */
export const getPasswordError = (password: string): string => {
  if (!password) return 'Please enter password';
  if (password.length < 8) return 'Password must be at least 8 characters';

  const hasNumber = /\d/.test(password);
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  const typeCount = [hasNumber, hasLetter, hasSpecialChar].filter(Boolean).length;
  if (typeCount < 2) {
    return 'Password must contain at least 2 of: numbers, letters, special characters';
  }

  return '';
};

/**
 * 验证表单字段
 * @param field 字段名
 * @param value 字段值
 * @returns 错误信息，如果没有错误则返回空字符串
 */
export const validateField = (field: string, value: any): string => {
  switch (field) {
    case 'email':
      if (!value) return 'Please enter email address';
      if (!validateEmail(value)) return 'Please enter a valid email address';
      return '';
    case 'password':
      return getPasswordError(value);
    case 'confirmPassword':
      if (!value) return 'Please confirm password';
      return '';
    case 'verificationCode':
      if (!value) return 'Please enter verification code';
      if (value.length !== 6) return 'Verification code should be 6 digits';
      return '';
    case 'username':
      // Username is optional, validate if provided
      if (value && value.length < 3) return 'Username must be at least 3 characters';
      if (value && value.length > 20) return 'Username cannot exceed 20 characters';
      if (value && !/^[a-zA-Z0-9_-]+$/.test(value))
        return 'Username can only contain letters, numbers, underscores and hyphens';
      return '';
    case 'displayName':
      // Display name is optional, validate if provided
      if (value && value.length > 50) return 'Display name cannot exceed 50 characters';
      return '';
    case 'terms':
      if (!value) return 'Please agree to Terms of Service and Privacy Policy';
      return '';
    default:
      return '';
  }
};

/**
 * 验证整个表单
 * @param formData 表单数据对象
 * @returns 错误信息对象，如果没有错误则为空对象
 */
export const validateForm = (formData: Record<string, any>): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.keys(formData).forEach((field) => {
    const error = validateField(field, formData[field]);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};
